import { zoomHint } from "../press-word.ts";
import { useScreenCue } from "./ScreenCue";
import { useExit } from "./useExit";
import { PixelIcon } from "./PixelArt";
import { createPortal } from "preact/compat";
import { useEffect, useRef, useState } from "preact/hooks";
import { useEscape } from "../escape";

interface Props {
  src: string;
  alt: string;
  onClose: () => void;
}

const MIN = 1;
const MAX = 6;
/** One wheel notch, and one press of + or -. */
const STEP = 1.15;

/** The zoom factor a key asks for at `scale` ("+" or "=" in, "-" out, "0" back to fit), or null for any other key. */
export function lightboxKeyZoom(key: string, scale: number): number | null {
  if (key === "+" || key === "=") return STEP;
  if (key === "-" || key === "_") return 1 / STEP;
  if (key === "0") return MIN / scale;
  return null;
}

/** How many times this device has opened the viewer; the zoom hint shows on the first few only. */
const OPENS_KEY = "op.lightboxOpens";
const HINT_OPENS = 3;

/** Counts this opening and says whether the hint still shows (the app explains once, or here, a few times). */
function countOpenForHint(): boolean {
  try {
    const seen = Number(localStorage.getItem(OPENS_KEY)) || 0;
    if (seen >= HINT_OPENS) return false;
    localStorage.setItem(OPENS_KEY, String(seen + 1));
    return true;
  } catch {
    return false;
  }
}

/**
 * Full-screen photo viewer with pinch-to-zoom, drag-to-pan, double-tap and
 * mouse-wheel zoom, and + / - / 0 on a keyboard. Page zoom is disabled app-wide, so this is where photos
 * get inspected. Gestures are handled with pointer events so the same code
 * serves touch, mouse and pen.
 */
export function Lightbox({ src, alt, onClose }: Props) {
  // The X and Escape leave through the stepped fade (src/components/useExit.ts).
  const exit = useExit(onClose);
  useScreenCue(exit.closing);
  const [showHint] = useState(countOpenForHint);
  const closeRef = useRef<HTMLButtonElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);

  // Mutable gesture state lives in a ref so pointer handlers never go stale.
  const g = useRef({
    scale: 1,
    tx: 0,
    ty: 0,
    pointers: new Map<number, { x: number; y: number }>(),
    startDist: 0,
    startScale: 1,
    startMid: { x: 0, y: 0 },
    startTx: 0,
    startTy: 0,
    lastTap: 0,
    moved: false,
  }).current;

  function apply() {
    const img = imgRef.current;
    if (!img) return;
    img.style.transform = `translate(${g.tx}px, ${g.ty}px) scale(${g.scale})`;
  }

  function clampPan() {
    const stage = stageRef.current;
    const img = imgRef.current;
    if (!stage || !img) return;
    // Keep at least some of the image on screen.
    const maxX = ((img.clientWidth * g.scale) - stage.clientWidth) / 2 + stage.clientWidth * 0.25;
    const maxY = ((img.clientHeight * g.scale) - stage.clientHeight) / 2 + stage.clientHeight * 0.25;
    g.tx = Math.max(-Math.max(maxX, 0), Math.min(Math.max(maxX, 0), g.tx));
    g.ty = Math.max(-Math.max(maxY, 0), Math.min(Math.max(maxY, 0), g.ty));
  }

  function zoomAt(factor: number, cx: number, cy: number) {
    const stage = stageRef.current;
    if (!stage) return;
    const rect = stage.getBoundingClientRect();
    const px = cx - rect.left - rect.width / 2;
    const py = cy - rect.top - rect.height / 2;
    const next = Math.max(MIN, Math.min(MAX, g.scale * factor));
    const ratio = next / g.scale;
    // Keep the point under the cursor/fingers fixed.
    g.tx = px - (px - g.tx) * ratio;
    g.ty = py - (py - g.ty) * ratio;
    g.scale = next;
    if (g.scale === MIN) {
      g.tx = 0;
      g.ty = 0;
    }
    clampPan();
    apply();
  }

  function onPointerDown(e: PointerEvent) {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    g.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    g.moved = false;
    if (g.pointers.size === 2) {
      const [a, b] = [...g.pointers.values()];
      g.startDist = Math.hypot(a.x - b.x, a.y - b.y);
      g.startScale = g.scale;
      g.startMid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      g.startTx = g.tx;
      g.startTy = g.ty;
    } else if (g.pointers.size === 1) {
      g.startMid = { x: e.clientX, y: e.clientY };
      g.startTx = g.tx;
      g.startTy = g.ty;
    }
  }

  function onPointerMove(e: PointerEvent) {
    if (!g.pointers.has(e.pointerId)) return;
    g.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (g.pointers.size === 2) {
      const [a, b] = [...g.pointers.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const next = Math.max(MIN, Math.min(MAX, (g.startScale * dist) / (g.startDist || 1)));
      const stage = stageRef.current!.getBoundingClientRect();
      // Zoom about the initial midpoint, then follow the midpoint as it drags.
      const px = g.startMid.x - stage.left - stage.width / 2;
      const py = g.startMid.y - stage.top - stage.height / 2;
      const ratio = next / g.startScale;
      g.tx = px - (px - g.startTx) * ratio + (mid.x - g.startMid.x);
      g.ty = py - (py - g.startTy) * ratio + (mid.y - g.startMid.y);
      g.scale = next;
      g.moved = true;
      clampPan();
      apply();
    } else if (g.pointers.size === 1 && g.scale > MIN) {
      const dx = e.clientX - g.startMid.x;
      const dy = e.clientY - g.startMid.y;
      if (Math.abs(dx) + Math.abs(dy) > 4) g.moved = true;
      g.tx = g.startTx + dx;
      g.ty = g.startTy + dy;
      clampPan();
      apply();
    }
  }

  function onPointerUp(e: PointerEvent) {
    g.pointers.delete(e.pointerId);
    if (g.pointers.size === 1) {
      // Went from pinch to single finger: restart the pan from here.
      const [p] = [...g.pointers.values()];
      g.startMid = { x: p.x, y: p.y };
      g.startTx = g.tx;
      g.startTy = g.ty;
      return;
    }
    if (g.pointers.size > 0) return;
    if (g.scale <= MIN + 0.02) {
      g.scale = MIN;
      g.tx = 0;
      g.ty = 0;
      apply();
    }
    if (!g.moved) {
      const now = Date.now();
      if (now - g.lastTap < 300) {
        g.lastTap = 0;
        if (g.scale > MIN) zoomAt(MIN / g.scale, e.clientX, e.clientY);
        else zoomAt(2.5, e.clientX, e.clientY);
      } else {
        g.lastTap = now;
      }
    }
  }

  function onWheel(e: WheelEvent) {
    e.preventDefault();
    zoomAt(e.deltaY < 0 ? STEP : 1 / STEP, e.clientX, e.clientY);
  }

  // Escape closes the viewer as the top layer (src/escape.ts), so a viewer over a sheet closes alone.
  // exit.close is stable, so the focus effect below still runs once per open.
  useEscape(!exit.closing, () => exit.close());

  useEffect(() => {
    const previousFocus = document.activeElement;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Tab") { e.preventDefault(); closeRef.current?.focus(); return; }
      // The keyboard's zoom, about the middle of the photo: + and - step as a wheel notch does, 0 fits it again.
      const factor = lightboxKeyZoom(e.key, g.scale);
      if (factor === null || e.ctrlKey || e.metaKey || e.altKey) return;
      const rect = stageRef.current?.getBoundingClientRect();
      if (!rect) return;
      e.preventDefault();
      zoomAt(factor, rect.left + rect.width / 2, rect.top + rect.height / 2);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
      if (previousFocus instanceof HTMLElement) previousFocus.focus();
    };
  }, []);

  // Rendered at the body, never inside the screen's scroller: on iOS a fixed overlay inside a
  // scrolling container is clipped to it, which hid the close button under the header.
  return createPortal(
    <div class={`lightbox${exit.cls}`} ref={exit.ref} role="dialog" aria-modal="true" aria-label={alt}>
      <button ref={closeRef} class="lightbox-close" type="button" onClick={() => exit.close()} aria-label="Close">
        <PixelIcon name="close" />
      </button>
      <div
        ref={stageRef}
        class="lightbox-stage"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={onWheel}
      >
        <img ref={imgRef} src={src} alt={alt} draggable={false} />
      </div>
      {showHint && <div class="lightbox-hint">{zoomHint()}</div>}
    </div>,
    document.body,
  );
}
